
/* 
Filters Docs: https://search.docs.reactify.com.au/components/filter
*/
import React, { useEffect, useRef } from "react";
import { FilterProps } from "@usereactify/search";
import { useAppContext } from "../../../context/AppContext"

export type FilterListProps = React.ComponentProps<
  NonNullable<FilterProps["renderFilterList"]>
>;

export const FilterList: React.FC<FilterListProps> = React.memo((props) => {
  const { accordionState, setAccordionState } = useAppContext();
  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (detailsRef.current) {
      const isOpen = accordionState[props.filter.name];
      if (isOpen !== undefined) {
        detailsRef.current.open = isOpen;
      }
    }
  }, [accordionState, props.filter.name]);

  const handleToggle = () => {
    if (detailsRef.current) {
      setAccordionState((prevState) => ({
        ...prevState,
        [props.filter.name]: detailsRef.current?.open,
      }));
    }
  };

  return (
    <div className="rs__filter accordion">
      <details ref={detailsRef} onToggle={handleToggle}>
        <summary className="rs__filter__name text-md">
          {props.filter.name}
          <span></span>
        </summary>
        <div className="collapsible__content accordion__content text-sm">
          {props.filter.displayView === 'list' ? (
            <FilterListList {...props} />
          ) : null}
          {props.filter.displayType === 'single' && props.filter.displayView === 'check' ? (
            <FilterListRadio {...props} />
          ) : null}
          {props.filter.displayType === 'multi' && props.filter.displayView === 'check' ? (
            <FilterListCheckbox {...props} />
          ) : null}
          {props.filter.displayView === 'swatch' ? (
            <FilterListSwatch {...props} />
          ) : null}
          {props.filter.displayView === 'box' ? (
            <FilterListBox {...props} />
          ) : null}
        </div>
      </details>
    </div>
  );
});

export const FilterListList: React.FC<FilterListProps> = (
  props
) => {
  return (
    <ul className="rs__filter__list">
      {props.filterListProps.options.map((option) => (
        <li
          key={option.key}
          className={`rs__filter__list-item ${
            option.checked ? "rs__filter__list-item-active" : ""
          }`}
        >
          <label
            className="rs__filter__list-item-label"
            onClick={() => props.filterListProps.handleChange(option.key)}
          >
            <span className="rs__filter__list-item-key">{option.label}</span>
            <span className="rs__filter__list-item-count">
              ({option.doc_count})
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
};

export const FilterListRadio: React.FC<FilterListProps> = (
  props
) => {
  return (
    <ul className="rs__filter__list">
      {props.filterListProps.options.map((option) => (
        <li
          key={option.key}
          className={`rs__filter__list-item ${
            option.checked ? "rs__filter__list-item-active" : ""
          }`}
        >
          <label className="rs__filter__list-item-label">
            <input
              className="rs__filter__list-item-input"
              value={option.key}
              type="radio"
              checked={option.checked}
              onChange={(event) =>
                props.filterListProps.handleChange(event.target.value)
              }
            />
            <span className="rs__filter__list-item-key">{option.label}</span>
            <span className="rs__filter__list-item-count">
              ({option.doc_count})
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
};

export const FilterListCheckbox: React.FC<FilterListProps> = (
  props
) => {
  return (
    <ul className="rs__filter__list">
      {props.filterListProps.options.map((option) => (
        <li
          key={option.key}
          className={`rs__filter__list-item ${
            option.checked ? "rs__filter__list-item-active" : ""
          }`}
        >
          <label className="rs__filter__list-item-label">
            <input
              className="rs__filter__list-item-input"
              value={option.key}
              type="checkbox"
              checked={option.checked}
              onChange={(event) =>
                props.filterListProps.handleChange(event.target.value)
              }
            />
            <span className="rs__filter__list-item-key">{option.label}</span>
            <span className="rs__filter__list-item-count">
              ({option.doc_count})
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
};

export const FilterListSwatch: React.FC<FilterListProps> = (
  props
) => {
  return (
    <div
      className="rs__filter__list"
      style={{
        display: "flex",
        flexWrap: "wrap",
      }}
    >
      {props.filterListProps.options.map((option) => (
        <div
          key={option.key}
          className={`rs__filter__list-item ${
            option.checked ? "rs__filter__list-item-active" : ""
          }`}
        >
          <div
            className="rs__filter__list-item-label"
            style={{
              display: "inline-block",
              width: "20px",
              height: "20px",
              marginRight: "2px",
              marginBottom: "2px",
              borderRadius: "50%",
              backgroundColor: option.key,
              border: option.checked
                ? "1px solid black"
                : "1px solid transparent",
            }}
            onClick={() => props.filterListProps.handleChange(option.key)}
          />
        </div>
      ))}
    </div>
  );
};

export const FilterListBox: React.FC<FilterListProps> = (
  props
) => {
  return (
    <div
      className="rs__filter__list"
      style={{
        display: "flex",
        flexWrap: "wrap",
      }}
    >
      {props.filterListProps.options.map((option) => (
        <div
          key={option.key}
          className={`rs__filter__list-item ${
            option.checked ? "rs__filter__list-item-active" : ""
          }`}
        >
          <div
            className="rs__filter__list-item-label"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "40px",
              height: "40px",
              marginRight: "2px",
              marginBottom: "2px",
              border: "1px solid black",
              overflow: "hidden",
              cursor: "pointer",
              color: option.checked ? "white" : "black",
              backgroundColor: option.checked ? "black" : "white",
            }}
            onClick={() => props.filterListProps.handleChange(option.key)}
          >
            {option.key}
          </div>
        </div>
      ))}
    </div>
  );
};
